/**
 * Animation Transform for Video Track.
 *
 * Uses RTCRtpScriptTransform to extract animation data from the video track.
 * The video track carries animation data in binary format (not actual video).
 *
 * @internal
 * @packageDocumentation
 */

import type { AnimationTransformEvent } from './types';
import { logger } from '../../utils';

export { METADATA_FIXED_HEADER_SIZE, PACKET_FLAGS } from './types';

/**
 * Check if browser supports RTCRtpScriptTransform.
 * @returns true if RTCRtpScriptTransform is available
 * @internal
 */
export function supportsScriptTransform(): boolean {
  return typeof RTCRtpScriptTransform !== 'undefined';
}

/**
 * Create RTCRtpScriptTransform for animation receiver (video track).
 * @param workerOrUrl - Either a Worker instance or a URL string to the worker script
 * @param onEvent - Optional callback for transform events
 * @returns RTCRtpScriptTransform instance
 * @internal
 */
export function createAnimationReceiverTransform(
  workerOrUrl: Worker | string | URL,
  onEvent?: (evt: AnimationTransformEvent) => void,
): RTCRtpScriptTransform {
  // Create worker if URL is provided, otherwise use the provided Worker
  const worker =
    workerOrUrl instanceof Worker
      ? workerOrUrl
      : new Worker(workerOrUrl, { type: 'module', name: 'animation-receiver' });

  worker.onmessage = (event) => {
    const { type } = event.data;

    if (type === 'ready') {
      onEvent?.({ type: 'ready', operation: 'receiver' });
    } else if (type === 'metadata') {
      onEvent?.(event.data);
    } else if (type === 'animation') {
      onEvent?.({
        type: 'animation',
        protobufData: event.data.protobufData,
        flags: event.data.flags,
        isIdle: event.data.isIdle,
        isStart: event.data.isStart,
        isEnd: event.data.isEnd,
        frameSeq: event.data.frameSeq,
        isRecovered: event.data.isRecovered,
      });
    } else if (type === 'transition') {
      onEvent?.({
        type: 'transition',
        protobufData: event.data.protobufData,
        flags: event.data.flags,
      });
    } else if (type === 'transitionEnd') {
      onEvent?.({
        type: 'transitionEnd',
        protobufData: event.data.protobufData,
        flags: event.data.flags,
      });
    } else if (type === 'idleStart') {
      onEvent?.({ type: 'idleStart' });
    } else if (type === 'error') {
      logger.error('AnimationTransform', 'Error:', event.data.error);
      onEvent?.(event.data);
    }
  };

  worker.onerror = (event) => {
    logger.error('AnimationTransform', 'Worker error:', event.message);
  };

  return new RTCRtpScriptTransform(worker, { operation: 'receiver' });
}
